import React, { useState, useEffect } from 'react';
import { errorLog } from '@/utils/logger';
import { useNavigate } from 'react-router-dom';
import { Users, Clock, AlertTriangle, CheckCircle, TrendingUp, DollarSign, Settings, Activity } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores/auth';
import { apiClient } from '@/services/api';

interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  pendingSessions: number;
  totalSessions: number;
  failedRewards: number;
  pendingRewards: number;
  pendingPayoutRequests: number;
}

const AdminPage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    activeUsers: 0,
    pendingSessions: 0,
    totalSessions: 0,
    failedRewards: 0,
    pendingRewards: 0,
    pendingPayoutRequests: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchAdminStats();
  }, []);

  const fetchAdminStats = async () => {
    try {
      setIsLoading(true);
      
      // Fetch users data
      const usersResponse = await apiClient.users.getAllUsers();
      const users = usersResponse.data || [];
      
      // Fetch pending sessions
      const pendingResponse = await apiClient.workSessions.getPending();
      const pendingSessions = pendingResponse.data || [];
      
      // Fetch all sessions for total count
      const allSessionsResponse = await apiClient.workSessions.getAll({ limit: 1 });
      
      // Fetch failed rewards
      const failedRewardsResponse = await apiClient.rewards.getFailed();
      const failedRewards = failedRewardsResponse.data || [];
      
      // Fetch pending rewards
      const pendingRewardsResponse = await apiClient.rewards.getPending();
      const pendingRewards = pendingRewardsResponse.data || [];

      // Fetch pending payout requests
      let pendingPayoutRequests: any[] = [];
      try {
        const payoutRequestsResponse = await apiClient.payoutRequests.getPending();
        pendingPayoutRequests = payoutRequestsResponse.data || [];
      } catch (e) {
        // Ignore if endpoint doesn't exist yet
      }

      setStats({
        totalUsers: users.length,
        activeUsers: users.filter((u: any) => u.isActive).length,
        pendingSessions: pendingSessions.length,
        totalSessions: allSessionsResponse.data?.length || 0,
        failedRewards: failedRewards.length,
        pendingRewards: pendingRewards.length,
        pendingPayoutRequests: pendingPayoutRequests.length,
      });
    } catch (error: any) {
      errorLog('Failed to fetch admin stats:', error);
      toast.error('Failed to load admin statistics');
    } finally {
      setIsLoading(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Access denied. Admin privileges required.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-white">Admin Dashboard</h1>
          <p className="text-lg text-white mt-1">System overview and management</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-4xl font-bold text-white">Admin Dashboard</h1>
        <p className="text-lg text-white mt-1">System overview and management</p>
      </div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalUsers}</p>
              <p className="text-xs text-green-600">{stats.activeUsers} active</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending Approvals</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.pendingSessions}</p>
              <p className="text-xs text-gray-500">awaiting review</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Sessions</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalSessions}</p>
              <p className="text-xs text-gray-500">all time</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DollarSign className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending Rewards</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.pendingRewards}</p>
              <p className="text-xs text-gray-500">awaiting distribution</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Failed Rewards</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.failedRewards}</p>
              <p className="text-xs text-red-600">need attention</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">System Status</p>
              <p className="text-lg font-semibold text-green-600">Operational</p>
              <p className="text-xs text-gray-500">all services running</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              User Management
            </h3>
            <p className="text-sm text-gray-600 mt-1">Manage user accounts and permissions</p>
          </div>
          <button 
            onClick={() => navigate('/admin/users')}
            className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 font-medium touch-manipulation"
          >
            Manage Users
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              Payout Requests
              {stats.pendingPayoutRequests > 0 && (
                <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                  {stats.pendingPayoutRequests}
                </span>
              )}
            </h3>
            <p className="text-sm text-gray-600 mt-1">Review and approve payout requests</p>
          </div>
          <button
            onClick={() => navigate('/admin/payouts')}
            className="w-full bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 font-medium touch-manipulation"
          >
            Manage Payouts
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-purple-600" />
              Reward Management
              {stats.failedRewards > 0 && (
                <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                  {stats.failedRewards}
                </span>
              )}
            </h3>
            <p className="text-sm text-gray-600 mt-1">Monitor and manage coin rewards</p>
          </div>
          <button
            onClick={() => navigate('/admin/rewards')}
            className="w-full bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 font-medium touch-manipulation"
          >
            View Rewards
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Settings className="h-5 w-5 text-gray-600" />
              Payout Settings
            </h3>
            <p className="text-sm text-gray-600 mt-1">Configure hybrid approval system</p>
          </div>
          <button
            onClick={() => navigate('/admin/settings')}
            className="w-full bg-gray-600 text-white px-4 py-3 rounded-lg hover:bg-gray-700 font-medium touch-manipulation"
          >
            Configure Settings
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Activity className="h-5 w-5 text-teal-600" />
              System Monitoring
            </h3>
            <p className="text-sm text-gray-600 mt-1">View metrics, alerts, and system health</p>
          </div>
          <button
            onClick={() => navigate('/admin/monitoring')}
            className="w-full bg-teal-600 text-white px-4 py-3 rounded-lg hover:bg-teal-700 font-medium touch-manipulation"
          >
            View Monitoring
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">System Overview</h3>
          <button
            onClick={fetchAdminStats}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Refresh
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Active Issues</h4>
            <div className="space-y-2">
              {stats.pendingPayoutRequests > 0 && (
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <span className="text-sm text-green-800">{stats.pendingPayoutRequests} payout requests awaiting approval</span>
                  <button
                    onClick={() => navigate('/admin/payouts')}
                    className="text-xs text-green-600 hover:text-green-700 font-medium"
                  >
                    Review →
                  </button>
                </div>
              )}
              {stats.failedRewards > 0 && (
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <span className="text-sm text-red-800">{stats.failedRewards} failed reward distributions</span>
                  <button
                    onClick={() => navigate('/admin/rewards')}
                    className="text-xs text-red-600 hover:text-red-700 font-medium"
                  >
                    Fix →
                  </button>
                </div>
              )}
              {stats.pendingPayoutRequests === 0 && stats.failedRewards === 0 && (
                <div className="p-3 bg-green-50 rounded-lg">
                  <span className="text-sm text-green-800">✅ No immediate issues detected</span>
                </div>
              )}
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Quick Stats</h4>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>Active Users:</span>
                <span className="font-medium">{stats.activeUsers}/{stats.totalUsers}</span>
              </div>
              <div className="flex justify-between">
                <span>Pending Rewards:</span>
                <span className="font-medium">{stats.pendingRewards}</span>
              </div>
              <div className="flex justify-between">
                <span>System Uptime:</span>
                <span className="font-medium text-green-600">99.9%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;